import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Folder, 
  FolderOpen, 
  File, 
  FileText, 
  Code, 
  Image, 
  Settings,
  ChevronRight,
  Shield,
  AlertTriangle,
  Loader2,
  HelpCircle
} from 'lucide-react';

interface FileNode {
  name: string;
  type: 'file' | 'folder';
  path: string;
  size?: number;
  lastModified?: Date;
  children?: FileNode[];
  expanded?: boolean;
  scanStatus?: 'clean' | 'infected' | 'scanning' | 'validating' | 'error' | 'unknown';
}

interface FileTreeProps {
  onFileSelect?: (filePath: string) => void;
  selectedFile?: string;
  files?: FileNode[];
}

const FileTree: React.FC<FileTreeProps> = ({ onFileSelect, selectedFile, files = [] }) => {
  const [fileTree, setFileTree] = useState<FileNode[]>([]);
  const [loading, setLoading] = useState(files.length === 0);

  useEffect(() => {
    if (files.length > 0) {
      setFileTree(files);
      setLoading(false);
    } else {
      loadFileTree();
    }
  }, [files]);

  const loadFileTree = async () => {
    try {
      const mockTree: FileNode[] = [
        {
          name: 'src',
          type: 'folder',
          path: '/src',
          expanded: true,
          children: [
            {
              name: 'components',
              type: 'folder',
              path: '/src/components',
              expanded: true,
              children: [
                { name: 'Button.tsx', type: 'file', path: '/src/components/Button.tsx' },
                { name: 'Modal.tsx', type: 'file', path: '/src/components/Modal.tsx' }
              ]
            },
            {
              name: 'pages',
              type: 'folder',
              path: '/src/pages',
              children: [
                { name: 'Home.tsx', type: 'file', path: '/src/pages/Home.tsx' },
                { name: 'About.tsx', type: 'file', path: '/src/pages/About.tsx' }
              ]
            },
            { name: 'App.tsx', type: 'file', path: '/src/App.tsx' },
            { name: 'index.tsx', type: 'file', path: '/src/index.tsx' }
          ]
        },
        {
          name: 'public',
          type: 'folder',
          path: '/public',
          children: [
            { name: 'index.html', type: 'file', path: '/public/index.html' },
            { name: 'favicon.ico', type: 'file', path: '/public/favicon.ico' }
          ]
        },
        { name: 'package.json', type: 'file', path: '/package.json' },
        { name: 'README.md', type: 'file', path: '/README.md' }
      ];
      
      setFileTree(mockTree);
    } catch (error) {
      console.error('Failed to load file tree:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFileIcon = (fileName: string, type: string) => {
    if (type === 'folder') return null;
    
    const ext = fileName.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'tsx':
      case 'jsx':
      case 'ts':
      case 'js':
        return <Code className="text-blue-400" size={14} />;
      case 'json':
        return <Settings className="text-yellow-400" size={14} />;
      case 'md':
        return <FileText className="text-green-400" size={14} />;
      case 'png':
      case 'jpg':
      case 'svg':
        return <Image className="text-purple-400" size={14} />;
      default:
        return <File className="text-slate-400" size={14} />;
    }
  };

  const getSecurityStatusIcon = (scanStatus?: string) => {
    switch (scanStatus) {
      case 'clean':
        return <Shield className="text-green-400" size={12} />;
      case 'infected':
        return <AlertTriangle className="text-red-400" size={12} />;
      case 'scanning':
        return <Loader2 className="text-blue-400 animate-spin" size={12} />;
      case 'validating':
        return <Loader2 className="text-yellow-400 animate-spin" size={12} />;
      case 'error':
        return <AlertTriangle className="text-orange-400" size={12} />;
      case 'unknown':
      default:
        return <HelpCircle className="text-gray-500" size={12} />;
    }
  };

  const toggleFolder = (path: string) => {
    const updateNode = (nodes: FileNode[]): FileNode[] => {
      return nodes.map(node => {
        if (node.path === path && node.type === 'folder') {
          return { ...node, expanded: !node.expanded };
        }
        if (node.children) {
          return { ...node, children: updateNode(node.children) };
        }
        return node;
      });
    };
    
    setFileTree(updateNode(fileTree));
  };

  const renderNode = (node: FileNode, depth: number = 0) => {
    const isSelected = selectedFile === node.path;
    
    return (
      <div key={node.path}>
        <motion.div
          className={`flex items-center space-x-2 py-1 px-2 rounded cursor-pointer transition-colors ${
            isSelected 
              ? 'bg-blue-600/20 text-blue-400' 
              : 'hover:bg-slate-700/50 text-slate-300'
          }`}
          style={{ paddingLeft: `${depth * 16 + 8}px` }}
          onClick={() => {
            if (node.type === 'folder') {
              toggleFolder(node.path);
            } else if (onFileSelect) {
              onFileSelect(node.path);
            }
          }}
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.99 }}
        >
          {node.type === 'folder' && (
            <motion.div
              animate={{ rotate: node.expanded ? 90 : 0 }}
              transition={{ duration: 0.2 }}
            >
              <ChevronRight size={14} />
            </motion.div>
          )}
          
          {node.type === 'folder' ? (
            node.expanded ? (
              <FolderOpen className="text-blue-400" size={14} />
            ) : (
              <Folder className="text-blue-400" size={14} />
            )
          ) : (
            getFileIcon(node.name, node.type)
          )}
          
          <span className="text-sm truncate flex-1">{node.name}</span>
          
          {node.type === 'file' && node.scanStatus && (
            <div className="ml-2 flex-shrink-0">
              {getSecurityStatusIcon(node.scanStatus)}
            </div>
          )}
        </motion.div>
        
        {node.type === 'folder' && node.expanded && node.children && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            {node.children.map(child => renderNode(child, depth + 1))}
          </motion.div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-4">
        <div className="animate-pulse space-y-2">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="h-6 bg-slate-700 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto">
      <div className="p-2 space-y-1">
        {fileTree.map(node => renderNode(node))}
      </div>
    </div>
  );
};

export default FileTree;
